import { useState } from "react";
import { ChevronDown, Eye, EyeOff, LineChart, PencilLine, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { OneTimeForm } from "@/components/OneTimeForm";
import { DailyEntryForm } from "@/components/DailyEntryForm";
import { api, apiError, fmtDay, fmtLakhValue, fmtPct } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const band = (v) =>
  v === null || v === undefined
    ? "hsl(var(--muted-foreground))"
    : v >= 95
      ? "hsl(var(--healthy))"
      : v >= 80
        ? "hsl(var(--watch))"
        : "hsl(var(--at-risk))";

const Stat = ({ label, value, colour }) => (
  <span className="block">
    <span className="font-data block text-[8.5px] uppercase tracking-widest text-muted-foreground">
      {label}
    </span>
    <span
      className="font-data block text-[15px] font-bold tabular-nums"
      style={colour ? { color: colour } : undefined}
    >
      {value}
    </span>
  </span>
);

/** One usecase under compliance watch: its position at a glance, and everything behind it. */
export const WatchRow = ({ card, onChanged, onTrend }) => {
  const { shows } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const can = card.can_record;

  const toggleVisible = async () => {
    setBusy(true);
    try {
      await api.patch(`/compliance/profiles/${card.project_id}`, { visible: !card.visible });
      toast.success(
        card.visible
          ? "Taken off the Dashboard. Every reading is kept."
          : "Back on the Dashboard.",
      );
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entry) => {
    setBusy(true);
    try {
      await api.delete(`/projects/${card.project_id}/compliance`, {
        params: { date: entry.date, phase: entry.phase, kpi: entry.kpi || "" },
      });
      toast.success(`${fmtDay(entry.date)} removed.`);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card" data-testid={`watch-${card.code}`}>
      <div className="flex flex-wrap items-center gap-4 px-5 py-4">
        <button
          type="button"
          data-testid={`watch-toggle-${card.code}`}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-[260px] flex-1 items-start gap-3 text-left"
        >
          <ChevronDown
            className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
          <span className="min-w-0">
            <span className="font-data flex flex-wrap items-center gap-1.5 text-[9.5px] uppercase tracking-wider">
              <span className="border border-border px-1.5 py-0.5 text-muted-foreground">
                {card.plant}
              </span>
              <span className="bg-primary px-1.5 py-0.5 text-primary-foreground">
                {card.phase || card.status || "—"}
              </span>
              <span className="text-muted-foreground">{card.code}</span>
              {!card.visible && (
                <span
                  className="border border-border px-1.5 py-0.5 text-muted-foreground"
                  data-testid={`watch-hidden-${card.code}`}
                >
                  off the dashboard
                </span>
              )}
            </span>
            <span className="mt-1 block font-display text-base font-bold leading-tight tracking-tight">
              {card.title}
            </span>
            <span className="mt-0.5 block text-[11px] text-muted-foreground">
              {card.kpi.name || "KPI not named"}
              {card.kpi.unit ? ` · ${card.kpi.unit}` : ""} · {card.compliance.days} day(s) on
              record · last filled {card.last_updated ? fmtDay(card.last_updated) : "never"}
              {card.last_updated_by ? ` by ${card.last_updated_by}` : ""}
            </span>
          </span>
        </button>

        <div className="flex flex-wrap items-center gap-6">
          <Stat
            label="Cumulative"
            value={fmtPct(card.compliance.cumulative_pct)}
            colour={band(card.compliance.cumulative_pct)}
          />
          <Stat label="Yesterday" value={fmtPct(card.compliance.yesterday_pct)} />
          <Stat label="Today" value={fmtPct(card.compliance.today_pct)} />
          <Stat
            label="Impact so far"
            value={fmtLakhValue(card.impact.total_lakh)}
            colour="hsl(var(--healthy))"
          />
          <Stat
            label="Target impact"
            value={card.impact.target_lakh ? fmtLakhValue(card.impact.target_lakh) : "N/A"}
            colour={card.impact.target_lakh ? undefined : "hsl(var(--watch))"}
          />
        </div>

        <div className="flex items-center gap-2">
          {shows("compliance_data.charts") && (
            <button
              type="button"
              data-testid={`watch-trend-${card.code}`}
              onClick={() => onTrend?.(card.project_id)}
              className="flex items-center gap-1.5 border border-border px-3 py-2 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted"
            >
              <LineChart className="h-3 w-3" /> Trend
            </button>
          )}
          {can && shows("compliance_data.visibility") && (
            <button
              type="button"
              data-testid={`watch-visible-${card.code}`}
              disabled={busy}
              onClick={toggleVisible}
              title={
                card.visible
                  ? "Take this odometer off the Dashboard — nothing is deleted"
                  : "Put this odometer back on the Dashboard"
              }
              className="flex items-center gap-1.5 border border-border px-3 py-2 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted disabled:opacity-40"
            >
              {card.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              {card.visible ? "Showing" : "Hidden"}
            </button>
          )}
        </div>
      </div>

      {open && (
        <div
          className="space-y-5 border-t border-border bg-muted/20 px-5 py-5"
          data-testid={`watch-open-${card.code}`}
        >
          <OneTimeForm
            card={card}
            canEdit={can}
            onSaved={onChanged}
            testId={`one-time-${card.code}`}
          />

          {can && shows("compliance_data.entries") && (
            <DailyEntryForm
              card={card}
              preset={edit}
              onCancel={() => setEdit(null)}
              onSaved={() => {
                setEdit(null);
                onChanged?.();
              }}
              testId={`daily-${card.code}`}
            />
          )}

          <div>
            <div className="label-caps">
              Every day on record · newest first ({card.entries.length})
            </div>
            <div className="mt-2 max-h-[320px] overflow-y-auto border border-border bg-card">
              <table className="w-full min-w-[620px] text-xs" data-testid={`entries-${card.code}`}>
                <thead className="sticky top-0 bg-muted">
                  <tr className="text-left">
                    <th className="label-caps px-3 py-2">Date</th>
                    <th className="label-caps px-3 py-2">Compliance</th>
                    <th className="label-caps px-3 py-2">KPI</th>
                    <th className="label-caps px-3 py-2">Impact ₹L</th>
                    <th className="label-caps px-3 py-2">Remark</th>
                    <th className="label-caps px-3 py-2">Filled on · by whom</th>
                    {can && <th className="label-caps px-3 py-2 text-right">Correct · remove</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {card.entries.map((e) => (
                    <tr key={`${e.date}-${e.phase}-${e.kpi || ""}`}>
                      <td className="font-data px-3 py-2 tabular-nums">{fmtDay(e.date)}</td>
                      <td
                        className="font-data px-3 py-2 font-bold tabular-nums"
                        style={{ color: band(e.pct) }}
                      >
                        {e.pct === null || e.pct === undefined ? "N/A" : `${e.pct}%`}
                      </td>
                      <td className="font-data px-3 py-2 tabular-nums">
                        {e.actual_value ?? "—"}
                      </td>
                      <td className="font-data px-3 py-2 tabular-nums">{e.impact_lakh ?? "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{e.remark || "—"}</td>
                      <td
                        className="px-3 py-2 text-muted-foreground"
                        data-testid={`entry-filled-${card.code}-${e.date}`}
                      >
                        {e.recorded_at ? fmtDay(e.recorded_at) : "—"}
                        {e.recorded_by ? ` · ${e.recorded_by}` : ""}
                      </td>
                      {can && (
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              type="button"
                              data-testid={`entry-edit-${card.code}-${e.date}`}
                              disabled={busy}
                              onClick={() => setEdit(e)}
                              className="text-muted-foreground transition-colors duration-200 hover:text-primary disabled:opacity-40"
                              title="Correct this day — it loads back into the form above"
                            >
                              <PencilLine className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              data-testid={`entry-remove-${card.code}-${e.date}`}
                              disabled={busy}
                              onClick={() => remove(e)}
                              className="text-muted-foreground transition-colors duration-200 hover:text-[hsl(var(--at-risk))] disabled:opacity-40"
                              title="Remove this day"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {!card.entries.length && (
                    <tr>
                      <td colSpan={can ? 7 : 6} className="p-5 text-center text-muted-foreground">
                        No day has been filled yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
